import React from "react"
import * as Yup from "yup"
import { useFormikContext } from "formik"

import CatchCard from "~/library/CatchCard"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import { Task } from "~/types/sponsorship/Task"
import FormikSelect from "~/library/FormikSelect"
import FormikTextarea from "~/library/FormikTextarea"
import { apiMedia } from "~/routes"
import FileUploader, { UploadedFile } from "~/library/UIkit/FileUploader/FileUploader"

const taskSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  description: Yup.string().max(1200, "Description must be less than 1200 characters"),
  status: Yup.string().required("Status is required"),
  mediaIds: Yup.array().of(Yup.string()),
})

const FileUploaderSection: React.FC<{ existingFiles: UploadedFile[] }> = ({ existingFiles }) => {
  const { values, setFieldValue } = useFormikContext<Task>()

  const handleFileUpload = (files: UploadedFile[]) => {
    const newMediaIds = files.map((file) => file.id)
    const currentMediaIds = values.mediaIds || []
    setFieldValue("mediaIds", [...currentMediaIds, ...newMediaIds])
  }

  const handleFileRemoved = (fileId: string) => {
    const currentMediaIds = values.mediaIds || []
    setFieldValue(
      "mediaIds",
      currentMediaIds.filter((id) => id !== fileId)
    )
  }

  return (
    <FileUploader
      className="mt-2"
      path={apiMedia.create.path({})}
      onUpload={handleFileUpload}
      onFileRemoved={handleFileRemoved}
      existingFiles={existingFiles}
      multiple={true}
      maxFiles={10}
      maxSize={25}
      accept="*"
    />
  )
}

interface TaskFormProps {
  onSubmit: (values: any) => void
  initialValues?: Partial<Task>
  isEditing?: boolean
  onCancel?: () => void
  onDelete?: () => void
  showCancel?: boolean
}

const TaskForm: React.FC<TaskFormProps> = ({
  onSubmit,
  initialValues,
  isEditing = false,
  onCancel,
  showCancel = true,
}) => {
  const defaultValues = {
    title: "",
    description: "",
    status: "not_started" as const,
    mediaIds: [],
  }

  const handleFormSubmit = (values: Task) => {
    const formData = {
      title: values.title,
      description: values.description || null,
      status: values.status,
      media_ids: values.mediaIds || [],
    }
    onSubmit(formData)
  }

  const statusOptions = [
    { value: "not_started", label: "Not Started" },
    { value: "upcoming", label: "Upcoming" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
  ]

  return (
    <FormikWrapper
      onSubmit={handleFormSubmit}
      validationSchema={taskSchema}
      initialValues={isEditing ? initialValues : defaultValues}
    >
      <CatchCard>
        <FormikInput name="title" label="Task Title" placeholder="Enter task title" required />
        <FormikTextarea
          name="description"
          label="Description"
          placeholder="Enter task description (optional)"
          rows={8}
          maxLength={1200}
        />
        <FormikSelect name="status" label="Status" noEmptyOption required options={statusOptions} />

        <CatchCard title="Attachments" style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}>
          <FileUploaderSection existingFiles={initialValues?.media || []} />
        </CatchCard>
      </CatchCard>

      <div className="d-flex">
        <div className="ms-auto">
          {showCancel && (
            <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn-primary">
            {isEditing ? "Update Task" : "Create Task"}
          </button>
        </div>
      </div>
    </FormikWrapper>
  )
}

export default TaskForm
